import { call, put, takeLatest, takeEvery, take, fork, cancel, all, select, race, delay } from 'redux-saga/effects'
import type { Task as SagaTask } from 'redux-saga'
import type { PayloadAction } from '@reduxjs/toolkit'
import { doc, getDoc, getDocs, collection, orderBy, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, serverTimestamp, runTransaction, documentId, deleteField } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { eventChannel } from 'redux-saga'
import { showToast } from '@/components/app/Toast/showToast'
import { db, functions, logAnalyticsEvent } from '../../lib/firebase'
import {
    startListening,
    createWorkspaceSaga,
    addStatusSaga,
    updateStatusSaga,
    deleteStatusSaga,
    addLabelSaga,
    updateLabelSaga,
    deleteLabelSaga,
    inviteMemberSaga,
    setWorkspaceAgentSaga,
    updateMemberRoleSaga,
    updateMemberStatusSaga,
    deleteInvitationSaga,
    updateWorkspaceNameSaga,
    updateWorkspaceAvatarSaga,
    updateWorkspaceCompanySizeSaga,
    updateWorkspaceTimezoneSaga,
    setWorkspaceTimezone,
    updateWorkspaceUrlSaga,
    updateWorkspaceIdentifierSaga,
    deleteWorkspaceSaga,
    getBillingUrlSaga,
    fetchUsageStatsSaga,
    initFigmaOAuthSaga,
    clearFigmaTokenSaga,
    initSlackOAuthSaga,
    disconnectSlackSaga,
    setWorkspace,
    setWorkspaceName,
    setWorkspaceAvatar,
    setWorkspaceCompanySize,
    setWorkspaceUrl,
    setWorkspaceIdentifier,
    setStatuses,
    setLabels,
    setMembers,
    setLoading,
    setError,
    setCreateWorkspaceError,
    setInviteLoading,
    setInviteError,
    clearWorkspace,
    setUsageStats,
    type UsageStats,
    type CreateWorkspacePayload,
} from './workspaceSlice'
import { setOpenModal, addLoadingKey, removeLoadingKey } from '../ui/uiSlice'
import type { Workspace, Status, Label, WorkspaceMember } from '../../contexts/WorkspaceContext'
import { listenAllProjects } from '../projects/projectsSaga'
import { listenAllLists } from '../lists/listsSaga'
import { listenAllTasks, listenCompletedTasks } from '../tasks/tasksSaga'
import { listenNotificationsUnwatched, listenNotificationsAll } from '../notifications/notificationsSaga'

import { createSnapshotChannel } from '../utils/createSnapshotChannel'
import { nextLabelColor } from '@/const/colors'
import { selectAuth } from '../auth/authSelectors'
import { selectWorkspace, selectStatuses, selectLabels, selectIsCurrentUserOwner } from './workspaceSelectors'
import type { RootState } from '../index'
import { clearProjects, setLoading as setProjectsLoading } from '../projects/projectsSlice'
import { clearLists } from '../lists/listsSlice'
import { clearTasks, updateTaskSaga } from '../tasks/tasksSlice'
import type { Task } from '../../contexts/TasksContext'
import { clearNotifications, clearAllNotifications } from '../notifications/notificationsSlice'
import { clearComments } from '../comments/commentsSlice'
import { getCachedWorkspaceId } from './workspaceUrlCache'
import { logTiming } from '@/utils/timing'
import { AnalyticsEvent, trackEvent, setPersonProps, clearJustSignedUp } from '@/lib/analytics'
import { workspaceNameError } from '@/utils/workspaceName'


function createDocChannel(docRef: any) {
    return eventChannel(emit => {
        const unsub = onSnapshot(
            docRef,
            (snapshot: any) => emit({ type: 'data', snapshot }),
            (error: any) => emit({ type: 'error', error })
        )
        return unsub
    })
}

// Workspace doc listener
function* listenWorkspaceDoc(workspaceId: string): Generator<any, void, any> {
    const channel: ReturnType<typeof createDocChannel> = yield call(
        createDocChannel,
        doc(db, 'workspaces', workspaceId)
    )
    let firstData = true
    try {
        while (true) {
            const event: any = yield take(channel)
            if (event.type === 'data') {
                if (event.snapshot.exists()) {
                    yield put(setWorkspace({ id: event.snapshot.id, ...event.snapshot.data() } as Workspace))
                } else {
                    yield put(setError('Workspace data not found'))
                }
                yield put(setLoading(false))
                if (firstData) {
                    logTiming('[workspace] listenWorkspaceDoc: first snapshot → setLoading(false)')
                    firstData = false
                    // Backfill the timezone for auto-created workspaces (signup no longer
                    // collects it via a form). Owner-only, silent, one-shot.
                    if (event.snapshot.exists() && !event.snapshot.data().timezone) {
                        const isOwner: boolean = yield select(selectIsCurrentUserOwner)
                        if (isOwner) {
                            const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
                            if (browserTz) {
                                yield put(setWorkspaceTimezone(browserTz))
                                yield call(() => updateDoc(doc(db, 'workspaces', workspaceId), { timezone: browserTz }))
                            }
                        }
                    }
                }
            } else {
                console.error('[workspaceSaga] listenWorkspaceDoc: channel error:', event.error)
                yield put(setError('Failed to load workspace'))
                yield put(setLoading(false))
            }
        }
    } finally {
        channel.close()
    }
}

// Statuses listener
function* listenStatuses(workspaceId: string): Generator<any, void, any> {
    const channel: ReturnType<typeof createSnapshotChannel> = yield call(
        createSnapshotChannel,
        query(collection(db, `workspaces/${workspaceId}/statuses`), orderBy('order'))
    )
    try {
        while (true) {
            const event: any = yield take(channel)
            if (event.type === 'data') {
                yield put(setStatuses(
                    event.snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() } as Status))
                ))
            }
        }
    } finally {
        channel.close()
    }
}

// Labels listener
function* listenLabels(workspaceId: string): Generator<any, void, any> {
    const channel: ReturnType<typeof createSnapshotChannel> = yield call(
        createSnapshotChannel,
        query(collection(db, `workspaces/${workspaceId}/labels`), orderBy('order'))
    )
    try {
        while (true) {
            const event: any = yield take(channel)
            if (event.type === 'data') {
                yield put(setLabels(
                    event.snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() } as Label))
                ))
            }
        }
    } finally {
        channel.close()
    }
}

type MemberDoc = { id: string; userId: string; workspaceId: string; role: string; status: string; createdAt?: { toDate: () => Date } }

// Firestore `in` limit = 30 values per query. Batch if workspace has more members.
async function fetchUsersByIds(userIds: string[]): Promise<Map<string, any>> {
    const byId = new Map<string, any>()
    const chunks: string[][] = []
    for (let i = 0; i < userIds.length; i += 30) chunks.push(userIds.slice(i, i + 30))

    await Promise.all(chunks.map(async (chunk) => {
        if (chunk.length === 0) return
        const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)))
        snap.docs.forEach((d) => byId.set(d.id, d.data()))
    }))

    return byId
}

async function enrichMemberDocs(memberDocs: MemberDoc[]): Promise<WorkspaceMember[]> {
    const userIds = [...new Set(memberDocs.map((m) => m.userId).filter(Boolean))]
    let usersById: Map<string, any>
    try {
        usersById = await fetchUsersByIds(userIds)
    } catch (err) {
        console.error('Error fetching users for members:', err)
        usersById = new Map()
    }

    return memberDocs.map((member) => {
        const createdAt = member.createdAt?.toDate() || null
        const userData = usersById.get(member.userId)
        if (userData) {
            return {
                ...member,
                createdAt,
                email: userData.email || '',
                fullName: userData.fullName || '',
                username: userData.username || '',
                color: userData.color || '#888',
                avatarImage: userData.avatarImage || null
            } as WorkspaceMember
        }
        return {
            ...member,
            createdAt,
            email: '',
            fullName: '',
            username: '',
            color: '#888',
            avatarImage: null
        } as WorkspaceMember
    })
}

// Initial load: getDocs(workspaceMembers) + in-query(users). Must be called BEFORE
// opening workspace listen channels — uses the fast REST transport, otherwise waits
// for the next Firestore long-polling wave (~2s in Safari).
function* preloadMembers(workspaceId: string): Generator<any, WorkspaceMember[], any> {
    const membersQuery = query(collection(db, 'workspaceMembers'), where('workspaceId', '==', workspaceId))
    logTiming('[members] preload → getDocs workspaceMembers')
    const snapshot: any = yield call(() => getDocs(membersQuery))
    const memberDocs = snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() } as MemberDoc))
    logTiming(`[members] preload ← getDocs done (${memberDocs.length}) → getDocs users (in-query)`)
    const enriched: WorkspaceMember[] = yield call(enrichMemberDocs, memberDocs)
    logTiming('[members] preload ← enrichMemberDocs done')
    return enriched
}

// Members init as its own fork: preload (REST) → set → realtime listener. Kept off the
// loader-gating path (projects) so the preload's ~450ms doesn't delay the project listeners.
function* loadAndListenMembers(workspaceId: string): Generator<any, void, any> {
    const initialMembers: WorkspaceMember[] = yield call(preloadMembers, workspaceId)
    yield put(setMembers(initialMembers))
    yield call(listenMembers, workspaceId, initialMembers)
}

// Realtime: members + invitations updates (initial load is done outside via preloadMembers)
function* listenMembers(workspaceId: string, initialMembers: WorkspaceMember[]): Generator<any, void, any> {
    logTiming('[members] listenMembers (realtime only) started')

    const membersQuery = query(collection(db, 'workspaceMembers'), where('workspaceId', '==', workspaceId))

    let registeredMembers: WorkspaceMember[] = initialMembers
    let pendingMembers: WorkspaceMember[] = []

    const updateMembers = function* () {
        const allMembers = [...registeredMembers, ...pendingMembers]
        allMembers.sort((a, b) => {
            const dateA = a.createdAt?.getTime?.() ?? 0
            const dateB = b.createdAt?.getTime?.() ?? 0
            return dateA - dateB
        })
        yield put(setMembers(allMembers))
    }

    const membersChannel: ReturnType<typeof createSnapshotChannel> = yield call(
        createSnapshotChannel,
        membersQuery
    )
    const invitationsChannel: ReturnType<typeof createSnapshotChannel> = yield call(
        createSnapshotChannel,
        query(collection(db, 'workspaceInvitations'), where('workspaceId', '==', workspaceId))
    )

    const membersFork: SagaTask = yield fork(function* () {
        try {
            while (true) {
                const event: any = yield take(membersChannel)
                if (event.type === 'data') {
                    const memberDocs = event.snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() } as MemberDoc))

                    registeredMembers = yield call(enrichMemberDocs, memberDocs)
                    yield* updateMembers()
                }
            }
        } finally {
            membersChannel.close()
        }
    })

    const invitationsFork: SagaTask = yield fork(function* () {
        try {
            while (true) {
                const event: any = yield take(invitationsChannel)
                if (event.type === 'data') {
                    pendingMembers = event.snapshot.docs.map((d: any) => {
                        const data = d.data()
                        const email = data.email || ''
                        const username = email.split('@')[0] || ''
                        const createdAt = data.createdAt?.toDate() || null

                        return {
                            id: d.id,
                            workspaceId: data.workspaceId,
                            userId: d.id,
                            role: 'member' as const,
                            status: 'pending' as const,
                            createdAt,
                            email,
                            fullName: '',
                            username,
                            color: '#888',
                            avatarImage: null
                        }
                    })
                    yield* updateMembers()
                }
            }
        } finally {
            invitationsChannel.close()
        }
    })

    // Wait for cancellation, then clean up forks
    try {
        yield take('NEVER_EMITTED_ACTION')
    } finally {
        yield cancel(membersFork)
        yield cancel(invitationsFork)
    }
}

// Wrapper — logs which listener fails
function* safeListener(name: string, saga: (...args: any[]) => Generator, ...args: any[]): Generator<any, void, any> {
    try {
        yield call(saga, ...args)
    } catch (err) {
        console.error(`[Listener FAILED] ${name}:`, err)
    }
}

// Main listener worker
function* listenWorkspaceWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const workspaceUrlId = action.payload
    logTiming(`[workspace] listenWorkspaceWorker start, urlId="${workspaceUrlId}"`)

    // Guest mode: data already in Redux from initialState/localStorage
    const authState = yield select(selectAuth)
    if (!authState) {
        logTiming('[workspace] auth is null — skipping (guest)')
        return
    }

    // If same workspace is already loaded, just re-establish listeners without clearing
    const currentWorkspace = yield select(selectWorkspace)
    const isSameWorkspace = currentWorkspace?.workspaceUrlId === workspaceUrlId

    if (!isSameWorkspace) {
        logTiming('[workspace] different workspace — clearing state')
        yield put(clearWorkspace())
        yield put(clearProjects())
        yield put(clearLists())
        yield put(clearTasks())
        yield put(clearNotifications())
        yield put(clearAllNotifications())
        yield put(clearComments())
        yield put(setLoading(true))
        yield put(setError(null))
    }

    // Step 1: Resolve workspaceUrlId -> workspaceId.
    // loadUserData already fetched the user's workspace docs and cached the urlId→id map,
    // so the common case skips a getDoc here. Fall back to a lookup for direct visits.
    let workspaceId: string | undefined = getCachedWorkspaceId(workspaceUrlId)
    if (!workspaceId) {
        try {
            logTiming(`[workspace] → getDoc workspaceUrlIds/${workspaceUrlId}`)
            const urlIdDoc: any = yield call(getDoc, doc(db, 'workspaceUrlIds', workspaceUrlId))
            logTiming(`[workspace] ← getDoc workspaceUrlIds done (exists=${urlIdDoc.exists()})`)
            if (!urlIdDoc.exists()) {
                yield put(setError('Workspace not found'))
                yield put(setLoading(false))
                return
            }
            workspaceId = urlIdDoc.data().workspaceId as string
        } catch (err) {
            console.error(`[workspace] Error fetching workspaceId:`, err)
            yield put(setError('Failed to load workspace'))
            yield put(setLoading(false))
            return
        }
    } else {
        logTiming(`[workspace] workspaceId resolved from cache (skipped getDoc)`)
    }

    const userId = authState.id

    logTiming(`[workspace] forking listeners (workspaceId=${workspaceId})`)

    // Fork essential listeners immediately. The global loader is gated on the first
    // PROJECTS snapshot, and members don't gate it — so members (preload + listener) run
    // concurrently via loadAndListenMembers instead of blocking the project listeners
    // behind a ~450ms members preload.
    const essentialForks: SagaTask[] = yield all([
        fork(safeListener, 'workspaceDoc', listenWorkspaceDoc, workspaceId),
        fork(safeListener, 'statuses', listenStatuses, workspaceId),
        fork(safeListener, 'labels', listenLabels, workspaceId),
        fork(safeListener, 'projects', listenAllProjects, workspaceId),
        fork(safeListener, 'lists', listenAllLists, workspaceId),
        fork(safeListener, 'tasks', listenAllTasks, workspaceId),
        fork(safeListener, 'members', loadAndListenMembers, workspaceId),
    ])

    // Deferred: comments + notifications load AFTER the loader is dismissed
    // to avoid inflating the initial Firestore wave on Safari (long-polling)
    const deferredTask: SagaTask = yield fork(startDeferredListeners, userId, workspaceId)

    // Keep listeners alive — takeLatest will cancel this worker
    // when a new startListening is dispatched (workspace switch)
    try {
        yield take('NEVER_EMITTED_ACTION')
    } finally {
        for (const f of essentialForks) {
            yield cancel(f)
        }
        yield cancel(deferredTask)
    }
}

function* startDeferredListeners(userId: string, workspaceId: string): Generator<any, void, any> {
    // Wait for the first PROJECTS snapshot before starting the heavy deferred listeners, so
    // completed tasks stay out of the cold Firestore wave on Safari long-polling (SCD-694).
    // This used to key off the splash (globalLoading), but the splash now drops earlier on
    // shell-ready (SCD-890) — so gate on the projects snapshot directly to keep the wave timing.
    // Bounded by a watchdog so a wedged projects channel can't block notifications forever.
    yield race({
        ready: call(function* (): Generator<any, void, any> {
            let state: any = yield select()
            while (state.projects.loading) {
                yield take(setProjectsLoading.type)
                state = yield select()
            }
        }),
        timeout: delay(8000),
    })

    logTiming('[deferred] starting notifications listeners')
    // Comments are no longer loaded workspace-wide — they load per task when a task is
    // opened (see commentsSaga startListening / TaskModalComments).
    const forks: SagaTask[] = yield all([
        // Completed tasks are loaded here (after first paint) instead of eagerly, so the
        // cold-load Firestore wave only carries the open task set — see listenAllTasks.
        fork(safeListener, 'completedTasks', listenCompletedTasks, workspaceId),
        fork(safeListener, 'notificationsUnwatched', listenNotificationsUnwatched, userId, workspaceId),
        fork(safeListener, 'notificationsAll', listenNotificationsAll, userId, workspaceId),
    ])

    try {
        yield take('NEVER_EMITTED_ACTION')
    } finally {
        for (const f of forks) {
            yield cancel(f)
        }
    }
}

// Generate prefix from workspace name
function generateTaskPrefix(name: string): string {
    const words = name.trim().split(/\s+/)
    let prefix: string

    if (words.length >= 3) {
        prefix = words.slice(0, 3).map(w => w[0]).join('')
    } else if (words.length === 2) {
        prefix = words[0].slice(0, 2) + words[1].slice(0, 2)
    } else {
        prefix = name.slice(0, 3)
    }

    // Task prefix is capped at 3 letters (SCD-753): identifiers like SCD-753 read cleanly only
    // with a short, fixed prefix. The 2-word branch can yield 4 chars, so slice after normalizing.
    return prefix.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3) || 'WS'
}

interface CreateWorkspaceResponse {
    workspaceId: string
    workspaceUrlId: string
}

function* createWorkspaceWorker(action: PayloadAction<CreateWorkspacePayload>) {
    const { name, workspaceUrlId, companySize } = action.payload
    const userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone

    yield put(addLoadingKey('create-workspace'))
    yield put(setCreateWorkspaceError(null))

    try {
        const createWorkspace = httpsCallable<
            { name: string; workspaceUrlId: string; workspaceTaskPrefixId: string; companySize?: string; timezone?: string },
            CreateWorkspaceResponse
        >(functions, 'createWorkspace')

        const workspaceTaskPrefixId = generateTaskPrefix(name)

        const result: { data: CreateWorkspaceResponse } = yield call(
            () => createWorkspace({
                name,
                workspaceUrlId,
                workspaceTaskPrefixId,
                companySize: companySize || undefined,
                timezone: userTimezone || undefined,
            })
        )

        void logAnalyticsEvent('gtm_workspace_create', {
            workspace_id: result.data.workspaceId,
            workspace_url_id: result.data.workspaceUrlId,
        })

        // Activation funnel: workspace created. A solo signup creates their own
        // workspace (owner), so this is the "didn't join via invite" path — clear
        // the just-signed-up flag so invite_accepted can't fire on the next load.
        trackEvent(AnalyticsEvent.WorkspaceCreated, { workspace_id: result.data.workspaceId })
        clearJustSignedUp()

        yield put(setOpenModal(null))
        showToast('Workspace created')
        window.location.href = `/${result.data.workspaceUrlId}/projects`
    } catch (error: any) {
        console.error('Create workspace error:', error)

        let errorMessage = 'Failed to create workspace'
        if (error.code === 'functions/already-exists') {
            errorMessage = 'This workspace URL is already taken'
        } else if (error.code === 'functions/invalid-argument') {
            errorMessage = error.message || 'Invalid input'
        } else if (error.message) {
            errorMessage = error.message
        }

        yield put(setCreateWorkspaceError(errorMessage))
    } finally {
        yield put(removeLoadingKey('create-workspace'))
    }
}

// --- Status CRUD workers ---

function* addStatusWorker(action: PayloadAction<{ name: string; color: string }>): Generator<any, void, any> {
    const workspaceId: string | undefined = yield select((state: RootState) => selectWorkspace(state)?.id)
    if (!workspaceId) return
    const statuses = yield select(selectStatuses)

    const doneStatus = statuses.find((s: any) => s.systemType === 'done')

    try {
        if (doneStatus) {
            const doneRef = doc(db, `workspaces/${workspaceId}/statuses`, doneStatus.id)
            yield call(() => updateDoc(doneRef, { order: doneStatus.order + 1 }))
            yield call(() => addDoc(collection(db, `workspaces/${workspaceId}/statuses`), {
                name: action.payload.name,
                color: action.payload.color,
                systemType: null,
                order: doneStatus.order,
                createdAt: serverTimestamp()
            }))
        } else {
            yield call(() => addDoc(collection(db, `workspaces/${workspaceId}/statuses`), {
                name: action.payload.name,
                color: action.payload.color,
                systemType: null,
                order: statuses.length,
                createdAt: serverTimestamp()
            }))
        }
        showToast('Status added')
    } catch (err) {
        console.error('Error adding status:', err)
    }
}

function* updateStatusWorker(action: PayloadAction<{ statusId: string; data: Partial<{ name: string; color: string }> }>): Generator<any, void, any> {
    const workspaceId: string | undefined = yield select((state: RootState) => selectWorkspace(state)?.id)
    if (!workspaceId) return

    try {
        const statusRef = doc(db, `workspaces/${workspaceId}/statuses`, action.payload.statusId)
        yield call(() => updateDoc(statusRef, action.payload.data))
        showToast('Status updated')
    } catch (err) {
        console.error('Error updating status:', err)
    }
}

function* deleteStatusWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const workspaceId: string | undefined = yield select((state: RootState) => selectWorkspace(state)?.id)
    if (!workspaceId) return

    try {
        const statusRef = doc(db, `workspaces/${workspaceId}/statuses`, action.payload)
        yield call(() => deleteDoc(statusRef))
        showToast('Status deleted')
    } catch (err) {
        console.error('Error deleting status:', err)
    }
}

// --- Label CRUD workers ---

function* addLabelWorker(action: PayloadAction<{ name: string; color?: string; attachToTaskId?: string }>): Generator<any, void, any> {
    const workspaceId: string | undefined = yield select((state: RootState) => selectWorkspace(state)?.id)
    if (!workspaceId) return
    const labels: Label[] = yield select(selectLabels)

    try {
        // The task picker creates labels without a color (no room for a swatch there), so pick
        // the least-used palette color here rather than leaving the chip on the default.
        const color = action.payload.color ?? nextLabelColor(labels.map(l => l.color))
        const ref: any = yield call(() => addDoc(collection(db, `workspaces/${workspaceId}/labels`), {
            name: action.payload.name,
            color,
            order: labels.length
        }))

        const { attachToTaskId } = action.payload
        if (attachToTaskId && ref?.id) {
            const task: Task | undefined = yield select((state: RootState) => state.tasks.byId[attachToTaskId])
            // Read the task's labels here rather than passing them in: the picker may have toggled
            // other labels between the dispatch and this write.
            yield put(updateTaskSaga({
                taskId: attachToTaskId,
                data: { labels: [...(task?.labels ?? []), ref.id] },
            }))
        } else {
            showToast('Label added')
        }
    } catch (err) {
        console.error('Error adding label:', err)
    }
}

function* updateLabelWorker(action: PayloadAction<{ labelId: string; data: Partial<{ name: string; color: string; order: number }> }>): Generator<any, void, any> {
    const workspaceId: string | undefined = yield select((state: RootState) => selectWorkspace(state)?.id)
    if (!workspaceId) return

    try {
        const labelRef = doc(db, `workspaces/${workspaceId}/labels`, action.payload.labelId)
        yield call(() => updateDoc(labelRef, action.payload.data))
        showToast('Label updated')
    } catch (err) {
        console.error('Error updating label:', err)
    }
}

function* deleteLabelWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const workspaceId: string | undefined = yield select((state: RootState) => selectWorkspace(state)?.id)
    if (!workspaceId) return

    try {
        const labelRef = doc(db, `workspaces/${workspaceId}/labels`, action.payload)
        yield call(() => deleteDoc(labelRef))
        showToast('Label deleted')
    } catch (err) {
        console.error('Error deleting label:', err)
    }
}

// --- Member management workers ---

function* inviteMemberWorker(action: PayloadAction<{ email: string; onInvited?: (userId: string) => void; addToAllProjects?: boolean }>): Generator<any, void, any> {
    const workspaceId: string | undefined = yield select((state: RootState) => selectWorkspace(state)?.id)
    if (!workspaceId) return

    yield put(setInviteLoading(true))
    yield put(setInviteError(null))

    try {
        const inviteMemberFn = httpsCallable(functions, 'inviteMember')
        const result: any = yield call(() => inviteMemberFn({
            workspaceId,
            email: action.payload.email,
            addToAllProjects: action.payload.addToAllProjects ?? false,
        }))
        const { status, userId, invitationId } = result.data
        const resolvedId: string = status === 'added' ? userId : invitationId
        action.payload.onInvited?.(resolvedId)
        // Activation funnel: invite sent. The person property powers the solo vs
        // team retention split in PostHog (the core solo-entry check).
        trackEvent(AnalyticsEvent.InviteSent, { workspace_invite: true })
        setPersonProps({ invited_teammate: true })
        showToast('Member invited')
    } catch (err: any) {
        if (err.code === 'functions/already-exists') {
            yield put(setInviteError('User is already a member or invitation already sent'))
        } else {
            yield put(setInviteError(err.message || 'Failed to send invitation'))
        }
    } finally {
        yield put(setInviteLoading(false))
    }
}

function* setWorkspaceAgentWorker(action: PayloadAction<{ enabled: boolean; onError?: () => void }>): Generator<any, void, any> {
    const workspaceId: string | undefined = yield select((state: RootState) => selectWorkspace(state)?.id)
    if (!workspaceId) {
        action.payload.onError?.()
        return
    }

    yield put(addLoadingKey('set-workspace-agent'))
    try {
        const fn = httpsCallable(functions, 'setWorkspaceAgent')
        yield call(() => fn({ workspaceId, enabled: action.payload.enabled }))
        showToast(action.payload.enabled ? 'plate agent added to the workspace' : 'plate agent removed')
    } catch (err: any) {
        console.error('Error updating workspace agent:', err)
        // Revert the caller's optimistic toggle (it never reverts on its own when
        // the real membership data doesn't change).
        action.payload.onError?.()
        showToast(err.message || 'Failed to update the agent', { type: 'error' })
    } finally {
        yield put(removeLoadingKey('set-workspace-agent'))
    }
}

function* updateMemberRoleWorker(action: PayloadAction<{ memberId: string; role: 'owner' | 'member' | 'guest' }>): Generator<any, void, any> {
    try {
        const memberRef = doc(db, 'workspaceMembers', action.payload.memberId)
        yield call(() => updateDoc(memberRef, { role: action.payload.role }))
        showToast('Member role updated')
    } catch (err) {
        console.error('Error updating member role:', err)
    }
}

function* updateMemberStatusWorker(action: PayloadAction<{ memberId: string; status: 'active' | 'suspended' }>): Generator<any, void, any> {
    try {
        const memberRef = doc(db, 'workspaceMembers', action.payload.memberId)
        yield call(() => updateDoc(memberRef, { status: action.payload.status }))
        showToast('Member status updated')
    } catch (err) {
        console.error('Error updating member status:', err)
    }
}

function* deleteInvitationWorker(action: PayloadAction<string>): Generator<any, void, any> {
    try {
        yield call(() => deleteDoc(doc(db, 'workspaceInvitations', action.payload)))
        showToast('Invitation deleted')
    } catch (err) {
        console.error('Error deleting invitation:', err)
    }
}

const RESERVED_WORKSPACE_URLS = ['workspace', 'admin', 'api', 'app', 'auth', 'login', 'logout', 'signup', 'register', 'settings', 'dashboard', 'home', 'help', 'support', 'billing', 'pricing', 'about', 'blog', 'careers', 'legal', 'privacy', 'terms', 'status']

function* updateWorkspaceUrlWorker(action: PayloadAction<{ value: string; onError: (msg: string | null) => void }>): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return

    const { value, onError } = action.payload
    const newUrl = value.trim().toLowerCase()
    if (!newUrl || newUrl === workspace.workspaceUrlId) {
        onError(null)
        return
    }

    onError(null)

    try {
        if (RESERVED_WORKSPACE_URLS.includes(newUrl)) {
            onError('This URL is already taken')
            return
        }

        const taken: any = yield call(getDoc, doc(db, 'workspaceUrlIds', newUrl))
        if (taken.exists()) {
            onError('This URL is already taken')
            return
        }

        yield call(() => runTransaction(db, async (tx) => {
            tx.delete(doc(db, 'workspaceUrlIds', workspace.workspaceUrlId))
            tx.set(doc(db, 'workspaceUrlIds', newUrl), { workspaceId: workspace.id })
            tx.update(doc(db, 'workspaces', workspace.id), { workspaceUrlId: newUrl })
        }))

        yield put(setWorkspaceUrl(newUrl))
        showToast('Workspace URL updated')
    } catch (err) {
        console.error('Error updating workspace URL:', err)
        onError('Failed to update URL')
    }
}

function* updateWorkspaceIdentifierWorker(action: PayloadAction<{ value: string; onError: (msg: string | null) => void }>): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return

    const { value, onError } = action.payload
    const newIdentifier = value.trim().toUpperCase()

    if (!newIdentifier) {
        onError('Identifier must be at least 1 character')
        return
    }

    onError(null)

    try {
        yield put(setWorkspaceIdentifier(newIdentifier))
        yield call(() => updateDoc(doc(db, 'workspaces', workspace.id), { workspaceTaskPrefixId: newIdentifier }))
        showToast('Identifier updated')
    } catch (err) {
        console.error('Error updating workspace identifier:', err)
        onError('Failed to update identifier')
    }
}

function* updateWorkspaceCompanySizeWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return
    try {
        yield put(setWorkspaceCompanySize(action.payload))
        yield call(() => updateDoc(doc(db, 'workspaces', workspace.id), { companySize: action.payload }))
        showToast('Company size updated')
    } catch (err) {
        console.error('Error updating workspace company size:', err)
    }
}

function* updateWorkspaceAvatarWorker(action: PayloadAction<string | null>): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return
    try {
        yield put(setWorkspaceAvatar(action.payload))
        yield call(() => updateDoc(doc(db, 'workspaces', workspace.id), { avatar: action.payload }))
        showToast('Avatar updated')
    } catch (err) {
        console.error('Error updating workspace avatar:', err)
    }
}

function* deleteWorkspaceWorker(): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return
    try {
        const deleteWorkspaceFn = httpsCallable(functions, 'deleteWorkspace')
        yield call(() => deleteWorkspaceFn({ workspaceId: workspace.id }))
        showToast('Workspace deleted')
        window.location.href = '/'
    } catch (err) {
        console.error('Error deleting workspace:', err)
    }
}

function* updateWorkspaceTimezoneWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return
    try {
        yield put(setWorkspaceTimezone(action.payload))
        yield call(() => updateDoc(doc(db, 'workspaces', workspace.id), {timezone: action.payload}))
        showToast('Timezone updated')
    } catch (err) {
        console.error('Error updating workspace timezone:', err)
    }
}

function* updateWorkspaceNameWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return

    // firestore.rules rejects this write outright (SCD-1534); catching it here
    // turns a silent permission-denied into a message, and avoids the optimistic
    // put that would otherwise leave the UI showing a name the server refused.
    const name = action.payload.trim()
    const error = workspaceNameError(name)
    if (error) {
        showToast(error)
        return
    }

    try {
        yield put(setWorkspaceName(name))
        yield call(() => updateDoc(doc(db, 'workspaces', workspace.id), { name }))
        showToast('Workspace name updated')
    } catch (err) {
        console.error('Error updating workspace name:', err)
    }
}

function* getBillingUrlWorker(): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return

    yield put(addLoadingKey('get-billing-url'))
    try {
        const fn = httpsCallable(functions, 'getBillingUrl')
        const result: any = yield call(() => fn({ workspaceId: workspace.id }))

        window.location.href = result.data.url;
    } catch (err: any) {
        console.error('Error getting billing URL:', err)
    } finally {
        yield put(removeLoadingKey('get-billing-url'))
    }
}

function* initFigmaOAuthWorker(): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return

    const figmaClientId = import.meta.env.VITE_FIGMA_CLIENT_ID
    if (!figmaClientId) {
        console.error('VITE_FIGMA_CLIENT_ID not configured')
        return
    }

    const nonce = crypto.randomUUID()
    const origin = window.location.origin
    const redirectUri = `${origin}/apps/figma/callback`
    const state = `${workspace.id}::${workspace.workspaceUrlId}::${nonce}::${origin}`

    yield put(addLoadingKey('figma-oauth-init'))
    try {
        yield call(() => updateDoc(doc(db, 'workspaces', workspace.id), { figmaOAuthPending: nonce }))
    } catch (err) {
        console.error('Error storing Figma OAuth nonce:', err)
        yield put(removeLoadingKey('figma-oauth-init'))
        return
    }
    yield put(removeLoadingKey('figma-oauth-init'))

    const params = new URLSearchParams({
        client_id: figmaClientId,
        redirect_uri: redirectUri,
        scope: 'file_content:read,file_metadata:read',
        state,
        response_type: 'code',
    })

    window.location.href = `https://www.figma.com/oauth?${params.toString()}`
}

function* fetchUsageStatsWorker(): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return
    try {
        const month = new Date().toISOString().slice(0, 7)
        const snap: any = yield call(() => getDoc(doc(db, 'usage_stats', workspace.id, 'months', month)))
        const data = snap.exists() ? snap.data() : {}
        const stats: UsageStats = {
            apiCalls: data.apiCalls ?? 0,
            webhookDeliveries: data.webhookDeliveries ?? 0,
            agentTokens: data.agentTokens ?? 0,
            agentRuns: data.agentRuns ?? 0,
        }
        yield put(setUsageStats(stats))
    } catch (err) {
        console.error('Error fetching usage stats:', err)
    }
}

function* clearFigmaTokenWorker(): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    if (!workspace) return
    try {
        yield call(() => updateDoc(doc(db, 'workspaces', workspace.id), {
            figmaAccessToken: deleteField(),
            figmaRefreshToken: deleteField(),
            figmaOAuthPending: deleteField(),
        }))
    } catch (err) {
        console.error('Error disconnecting Figma:', err)
    }
}

// Slack install (mirrors initFigmaOAuthWorker). We write a CSRF nonce + the initiating
// user on the workspace doc, then redirect to Slack's OAuth v2 consent screen. The
// slackOAuthCallback function validates the nonce and persists the install server-side.
function* initSlackOAuthWorker(): Generator<any, void, any> {
    const workspace: any = yield select((state) => state.workspace.workspace)
    const uid: string | undefined = yield select((state: RootState) => state.auth?.id)
    if (!workspace) return

    const slackClientId = import.meta.env.VITE_SLACK_CLIENT_ID
    if (!slackClientId) {
        console.error('VITE_SLACK_CLIENT_ID not configured')
        return
    }

    const nonce = crypto.randomUUID()
    const origin = window.location.origin
    const redirectUri = `${origin}/slack/oauth/callback`
    const state = `${workspace.id}::${workspace.workspaceUrlId}::${nonce}::${origin}`

    // Bot scopes — must match the app manifest. Keep in sync with manifest.*.json.
    // users:read + users:read.email power the email-based identity match (SCD-790).
    const scope = [
        'commands',
        'chat:write',
        'incoming-webhook',
        'app_mentions:read',
        'im:write',
        'users:read',
        'users:read.email',
        'files:read',
        'channels:history',
        'groups:history',
    ].join(',')

    yield put(addLoadingKey('slack-oauth-init'))
    try {
        yield call(() => updateDoc(doc(db, 'workspaces', workspace.id), {
            slackOAuthPending: nonce,
            slackOAuthInitiatedBy: uid ?? null,
        }))
    } catch (err) {
        console.error('Error storing Slack OAuth nonce:', err)
        yield put(removeLoadingKey('slack-oauth-init'))
        return
    }
    yield put(removeLoadingKey('slack-oauth-init'))

    const params = new URLSearchParams({
        client_id: slackClientId,
        scope,
        redirect_uri: redirectUri,
        state,
    })

    window.location.href = `https://slack.com/oauth/v2/authorize?${params.toString()}`
}

function* disconnectSlackWorker(): Generator<any, void, any> {
    const workspaceId: string | undefined = yield select((state: RootState) => selectWorkspace(state)?.id)
    if (!workspaceId) return

    yield put(addLoadingKey('slack-disconnect'))
    try {
        const fn = httpsCallable(functions, 'disconnectSlack')
        yield call(() => fn({ workspaceId }))
        showToast('Slack disconnected')
    } catch (err: any) {
        console.error('Error disconnecting Slack:', err)
        showToast(err.message || 'Failed to disconnect Slack', { type: 'error' })
    } finally {
        yield put(removeLoadingKey('slack-disconnect'))
    }
}

export function* watchWorkspaceSaga() {
    yield takeLatest(startListening.type, listenWorkspaceWorker)
    yield takeLatest(createWorkspaceSaga.type, createWorkspaceWorker)
    yield takeLatest(addStatusSaga.type, addStatusWorker)
    yield takeLatest(updateStatusSaga.type, updateStatusWorker)
    yield takeLatest(deleteStatusSaga.type, deleteStatusWorker)
    // takeEvery, not takeLatest: each label write is a discrete create/rename/recolor/delete, and
    // a second one dispatched right after (rename then recolor, or two quick creates) must not
    // cancel the first mid-write — the same reason description saves got their own worker (SCD-905).
    yield takeEvery(addLabelSaga.type, addLabelWorker)
    yield takeEvery(updateLabelSaga.type, updateLabelWorker)
    yield takeEvery(deleteLabelSaga.type, deleteLabelWorker)
    yield takeLatest(inviteMemberSaga.type, inviteMemberWorker)
    yield takeLatest(setWorkspaceAgentSaga.type, setWorkspaceAgentWorker)
    yield takeLatest(updateMemberRoleSaga.type, updateMemberRoleWorker)
    yield takeLatest(updateMemberStatusSaga.type, updateMemberStatusWorker)
    yield takeLatest(deleteInvitationSaga.type, deleteInvitationWorker)
    yield takeLatest(deleteWorkspaceSaga.type, deleteWorkspaceWorker)
    yield takeLatest(updateWorkspaceNameSaga.type, updateWorkspaceNameWorker)
    yield takeLatest(updateWorkspaceAvatarSaga.type, updateWorkspaceAvatarWorker)
    yield takeLatest(updateWorkspaceCompanySizeSaga.type, updateWorkspaceCompanySizeWorker)
    yield takeLatest(updateWorkspaceTimezoneSaga.type, updateWorkspaceTimezoneWorker)
    yield takeLatest(updateWorkspaceUrlSaga.type, updateWorkspaceUrlWorker)
    yield takeLatest(updateWorkspaceIdentifierSaga.type, updateWorkspaceIdentifierWorker)
    yield takeLatest(getBillingUrlSaga.type, getBillingUrlWorker)
    yield takeLatest(fetchUsageStatsSaga.type, fetchUsageStatsWorker)
    yield takeLatest(initFigmaOAuthSaga.type, initFigmaOAuthWorker)
    yield takeLatest(clearFigmaTokenSaga.type, clearFigmaTokenWorker)
    yield takeLatest(initSlackOAuthSaga.type, initSlackOAuthWorker)
    yield takeLatest(disconnectSlackSaga.type, disconnectSlackWorker)
}
